import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState, ErrorState, Skeleton } from '../../../components/ui/states';
import { errorMessage } from '../../../lib/error-message';
import { collaborationApi, collaborationKeys } from '../api';
import { MentionText } from '../mention-text';
import { CommentComposer } from './comment-composer';

export function CommentThread({
  taskId,
  projectId,
  currentUserId,
  canDelete = true,
}: {
  taskId: string;
  projectId: string;
  currentUserId: string;
  /**
   * Cho phép xoá bình luận của chính mình. Mặc định bật — hành vi cũ của
   * board/gantt không đổi. Tắt ở popup "Việc của tôi" (`task-detail-popup.tsx`):
   * trang đó là chỗ báo cáo tiến độ, không phải nơi quản lý nội dung trao đổi
   * của dự án, và xoá một bình luận là xoá dấu vết trao đổi mà người khác có
   * thể đang trỏ tới.
   */
  canDelete?: boolean;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const comments = useQuery({
    queryKey: collaborationKeys.comments(taskId),
    queryFn: () => collaborationApi.comments(taskId),
  });

  /**
   * Sau khi ghi phải làm mới cả **lịch sử thay đổi**, không chỉ danh sách bình
   * luận: bình luận nằm cùng aggregate `task` nên nó xuất hiện trong activity
   * feed ngay bên cạnh. Quên bước này thì hai tab cùng màn hình lệch nhau.
   */
  const invalidate = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: collaborationKeys.comments(taskId) }),
      queryClient.invalidateQueries({ queryKey: collaborationKeys.activity(taskId) }),
    ]);
  };

  const add = useMutation({
    mutationFn: (body: string) => collaborationApi.addComment(taskId, body),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (commentId: string) => collaborationApi.deleteComment(commentId),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-4">
      {comments.isPending && <Skeleton className="h-32 w-full" />}
      {comments.isError && (
        <ErrorState
          message={errorMessage(comments.error, t)}
          onRetry={() => void comments.refetch()}
        />
      )}

      {comments.isSuccess &&
        (comments.data.length === 0 ? (
          <EmptyState
            title={t('collaboration.noComments')}
            description={t('collaboration.noCommentsHelp')}
          />
        ) : (
          <ul className="space-y-3">
            {comments.data.map((comment) => (
              <li key={comment.id} className="rounded-DEFAULT border border-border p-3">
                <div className="mb-1 flex items-baseline gap-2 text-xs text-muted-foreground">
                  {/* Bình luận kéo về (APM-SYNC-f): tên người viết gốc + nguồn, không
                      hiện như người tạo kết nối (`authorId`) viết. */}
                  <span className="font-medium text-foreground">
                    {(comment.external ? comment.external.authorName : comment.authorName) ??
                      t('collaboration.unknownAuthor')}
                  </span>
                  {comment.external && (
                    <span
                      className="rounded-sm bg-muted px-1.5 py-0.5 text-[11px] font-medium"
                      title={t('collaboration.syncedFromTitle', {
                        source: comment.external.source,
                      })}
                    >
                      {t('collaboration.syncedFrom', { source: comment.external.source })}
                    </span>
                  )}
                  <span>{new Date(comment.createdAt).toLocaleString('vi-VN')}</span>
                  {comment.editedAt && <span>· {t('collaboration.edited')}</span>}

                  {canDelete && comment.authorId === currentUserId && (
                    <button
                      type="button"
                      className="ml-auto text-muted-foreground hover:text-rag-red"
                      aria-label={t('collaboration.deleteComment')}
                      onClick={() => remove.mutate(comment.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  )}
                </div>

                <div className="text-sm">
                  <MentionText body={comment.body} currentUserId={currentUserId} />
                </div>
              </li>
            ))}
          </ul>
        ))}

      <CommentComposer
        projectId={projectId}
        disabled={add.isPending}
        onSubmit={async (body) => {
          await add.mutateAsync(body);
        }}
      />

      {add.isError && <ErrorState message={errorMessage(add.error, t)} />}
    </div>
  );
}
